/**
 * 本から TODO を作るフォーマットの編集（追加・変更・削除・並べ替え）。
 *
 * フォーマットは入力欄からフォーカスが外れたとき、または編集画面を閉じるときに保存する。
 * プロジェクトは TODO に付いているものから選び、選んだらすぐ保存する。
 * 「新しいフォーマット」も、追加を押さずに閉じたら追加する。
 */

import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TodoBookTemplate, TodoBookTemplateInput } from '@/api/types/todo-book-template'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useBookTodoTemplateStore } from '@/store/useBookTodoTemplateStore'

import { BOOK_TODO_PLACEHOLDERS, DEFAULT_BOOK_TODO_FORMAT } from '../lib/book-todo-format'

// Radix Select は空文字を値にできないので、「なし」を表す値を別に持つ
const NO_PROJECT = '__none__'

type RowSaved = { format: string; project: string | null }

const TemplateRow = ({ template, index, count }: { template: TodoBookTemplate; index: number; count: number }) => {
  const updateTemplate = useBookTodoTemplateStore((s) => s.updateTemplate)
  const deleteTemplate = useBookTodoTemplateStore((s) => s.deleteTemplate)
  const moveTemplate = useBookTodoTemplateStore((s) => s.moveTemplate)
  const projects = useBookTodoTemplateStore((s) => s.projects)
  const [format, setFormat] = useState(template.format)
  const [project, setProject] = useState<string | null>(template.project)
  const templateId = template.id
  // 最後に送った値。blur と閉じるときの両方で保存が走っても同じ PATCH を二度送らない
  const savedRef = useRef<RowSaved>({ format: template.format, project: template.project })
  const formatRef = useRef(format)
  useEffect(() => {
    formatRef.current = format
  })

  const save = useCallback(
    (patch: Partial<TodoBookTemplateInput>) => {
      // 削除した行は保存しない
      if (!useBookTodoTemplateStore.getState().templates.some((t) => t.id === templateId)) return
      const previous = savedRef.current
      savedRef.current = { ...previous, ...patch }
      updateTemplate(templateId, patch).catch(() => {
        savedRef.current = previous
        toast.error('フォーマットの保存に失敗しました')
      })
    },
    [templateId, updateTemplate],
  )

  const flushFormat = useCallback(() => {
    const trimmed = formatRef.current.trim()
    if (!trimmed || trimmed === savedRef.current.format) return
    save({ format: trimmed })
  }, [save])

  // iOS Safari はボタンをタップしても入力欄の blur が起きないため、編集を終える・シートを閉じるときにも保存する
  useEffect(() => () => flushFormat(), [flushFormat])

  const commitFormat = () => {
    if (!format.trim()) setFormat(savedRef.current.format)
    flushFormat()
  }

  const changeProject = (value: string) => {
    const next = value === NO_PROJECT ? null : value
    setProject(next)
    if (next !== savedRef.current.project) save({ project: next })
  }

  // 今のプロジェクトが TODO から消えていても選択肢に残す
  const options = project && !projects.includes(project) ? [project, ...projects] : projects

  return (
    <li className="space-y-1.5 rounded-md border border-border/60 p-2">
      <div className="flex items-center gap-1">
        <Input
          aria-label={`フォーマット ${index + 1}`}
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          onBlur={commitFormat}
          className="h-8 flex-1 text-sm"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="上へ"
          disabled={index === 0}
          onClick={() => void moveTemplate(template.id, -1).catch(() => toast.error('並べ替えに失敗しました'))}
        >
          <ArrowUp className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="下へ"
          disabled={index === count - 1}
          onClick={() => void moveTemplate(template.id, 1).catch(() => toast.error('並べ替えに失敗しました'))}
        >
          <ArrowDown className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive"
          aria-label={`${template.format} を削除`}
          onClick={() => void deleteTemplate(template.id).catch(() => toast.error('削除に失敗しました'))}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
      <Select value={project ?? NO_PROJECT} onValueChange={changeProject}>
        <SelectTrigger aria-label="プロジェクト" size="sm" className="h-7 w-full text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_PROJECT}>なし</SelectItem>
          {options.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </li>
  )
}

export const BookTodoTemplateEditor = () => {
  const templates = useBookTodoTemplateStore((s) => s.templates)
  const createTemplate = useBookTodoTemplateStore((s) => s.createTemplate)
  const [newFormat, setNewFormat] = useState('')
  const [pending, setPending] = useState(false)
  const newFormatRef = useRef('')
  useEffect(() => {
    newFormatRef.current = newFormat
  })

  // 追加を押さずに編集を終えた・シートを閉じたときも、書きかけのフォーマットを追加する
  useEffect(
    () => () => {
      const format = newFormatRef.current.trim()
      if (!format) return
      createTemplate({ format }).catch(() => toast.error('フォーマットの追加に失敗しました'))
    },
    [createTemplate],
  )

  const add = async () => {
    const format = newFormat.trim()
    if (!format) return
    // 先に空にして、追加の途中で閉じても二重に追加しないようにする
    setNewFormat('')
    newFormatRef.current = ''
    setPending(true)
    try {
      await createTemplate({ format })
    } catch {
      setNewFormat(format)
      toast.error('フォーマットの追加に失敗しました')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="space-y-3">
      {templates.length === 0 && (
        <p className="text-xs text-muted-foreground">
          フォーマットが無いときは「{DEFAULT_BOOK_TODO_FORMAT}」を使います。
        </p>
      )}
      <ul className="space-y-2">
        {templates.map((t, i) => (
          <TemplateRow key={t.id} template={t} index={i} count={templates.length} />
        ))}
      </ul>
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <Input
            aria-label="新しいフォーマット"
            placeholder={DEFAULT_BOOK_TODO_FORMAT}
            value={newFormat}
            onChange={(e) => setNewFormat(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
              e.preventDefault()
              void add()
            }}
            className="h-8 flex-1 text-sm"
          />
          <Button
            type="button"
            size="sm"
            aria-label="フォーマットを追加"
            disabled={pending || !newFormat.trim()}
            onClick={() => void add()}
          >
            追加
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {BOOK_TODO_PLACEHOLDERS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setNewFormat((v) => `${v}${p.key}`)}
              className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
            >
              {p.key} {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
